const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const pad = (value: number, length = 2) => String(value).padStart(length, '0');

/** The explicit zone is also shown beside the Helpdesk date controls. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** Match the backend's positive four-digit UTC year range after offset conversion. */
export function isSupportedUtcInstant(instant: number): boolean {
  const year = new Date(instant).getUTCFullYear();
  return Number.isFinite(instant) && year >= 1 && year <= 9999;
}

/** Format a stored instant for a datetime-local input without losing seconds or milliseconds. */
export function localDateTime(instant: string, timeZone: string): string {
  const date = new Date(instant);
  const formatter = new Intl.DateTimeFormat('en-CA-u-ca-iso8601-nu-latn', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const parts = Object.fromEntries(
    formatter.formatToParts(date).map(({ type, value }) => [type, value])
  );
  return `${parts.year.padStart(4, '0')}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}.${pad(date.getUTCMilliseconds(), 3)}`;
}

type LocalDateResult =
  | { value: string | null; error?: never }
  | { error: 'invalid' | 'nonexistent' | 'ambiguous'; value?: never };

/** Resolve a wall time only when it denotes exactly one instant in the displayed browser zone. */
export function resolveLocalDateTime(value: string, timeZone: string): LocalDateResult {
  if (value === '') return { value: null };
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?$/u.test(value))
    return { error: 'invalid' };
  const wallTime = Date.parse(`${value}Z`);
  if (!Number.isFinite(wallTime)) return { error: 'invalid' };
  const canonical = new Date(wallTime).toISOString().slice(0, -1);
  if (!canonical.startsWith(value)) return { error: 'invalid' };

  // Probe both sides of timezone transitions. Each distinct offset is round-tripped;
  // Date's default gap normalization and fold choice are never used as a decision.
  const offsets = new Set<number>();
  for (let hours = -48; hours <= 48; hours += 6) {
    const probe = wallTime + hours * HOUR_MS;
    const offset = probe - Date.parse(`${localDateTime(new Date(probe).toISOString(), timeZone)}Z`);
    if (Number.isFinite(offset)) offsets.add(offset);
  }
  const matches = [...offsets].filter(
    (offset) => localDateTime(new Date(wallTime + offset).toISOString(), timeZone) === canonical
  );
  if (matches.length === 0) return { error: 'nonexistent' };
  if (matches.length !== 1) return { error: 'ambiguous' };
  const offset = matches.at(0);
  if (offset === undefined || !isSupportedUtcInstant(wallTime + offset))
    return { error: 'invalid' };
  const offsetMinutes = -offset / MINUTE_MS;
  if (!Number.isInteger(offsetMinutes)) return { error: 'invalid' };
  const magnitude = Math.abs(offsetMinutes);
  return {
    value: `${canonical}${offsetMinutes < 0 ? '-' : '+'}${pad(Math.floor(magnitude / 60))}:${pad(magnitude % 60)}`,
  };
}

export function isHelpdeskClosed(task: {
  isClosed?: boolean | null;
  status: { dbValue: string } | null;
}): boolean {
  return (
    task.isClosed === true || task.status?.dbValue === 'done' || task.status?.dbValue === 'canceled'
  );
}
